// Org-wide operations view: every long-running backend task (AI derives,
// document ingests, …) — running ones with their live log feed, plus recent
// history with runtime and cost. Reads the org task registry
// (orgs/{org}/tasks, written by backend/shared/taskProgress.js) via
// onSnapshot, so it IS realtime. Admin/owner view.
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import firebase from '../firebaseConfig';
import { useOrg } from '../data/orgs';
import { useCollection } from '../data/firestore';
import { useVisibleDeals } from '../data/permissions';
import TaskProgress from '../components/TaskProgress';
import FitText from '../components/FitText';

const API = process.env.REACT_APP_UPLOAD_API || '';

// Spoken answers should start well under this; p95 above it gets flagged.
const VOICE_TOOL_BUDGET_MS = 10000;

// Per-tool latency/error rollup from /voice/stats (voiceInteractions +
// voiceSessions). Loaded once on mount — this is an ops card, not realtime.
function VoiceHealth() {
  const [stats, setStats] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const user = firebase.auth().currentUser;
        const token = user ? await user.getIdToken() : null;
        const r = await fetch(`${API}/voice/stats`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: '{}',
        });
        if (!r.ok) throw new Error(`stats failed (${r.status})`);
        const data = await r.json();
        if (alive) setStats(data);
      } catch (_) {
        if (alive) setFailed(true);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (!API || failed) return null;

  const fmtMs = (ms) => (ms == null ? '—' : ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`);

  return (
    <section className="u-stack" style={{ '--gap': 'var(--sp-3)' }}>
      <div className="u-stack" style={{ '--gap': '2px' }}>
        <h2 style={{ margin: 0, fontSize: 'var(--fs-lg)' }}>Voice agent health</h2>
        <span className="u-xs u-muted">
          {stats
            ? `Last ${stats.sampled} tool calls · ${stats.sessions} recent sessions · ${stats.reconnects} reconnects · ${stats.watchdogFires} watchdog fires`
            : 'Loading…'}
        </span>
      </div>
      {stats === null && <div className="cs-skeleton" style={{ height: 60 }} />}
      {stats !== null && !stats.tools.length && <div className="cs-empty">No voice tool calls yet.</div>}
      {stats !== null && stats.tools.length > 0 && (
        <div className="cs-card" style={{ padding: 'var(--sp-3) var(--sp-4)' }}>
          <table className="u-small u-tabular" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr className="u-xs u-muted" style={{ textAlign: 'left' }}>
                <th style={{ padding: '4px 8px 6px 0', fontWeight: 600 }}>Tool</th>
                <th style={{ padding: '4px 8px 6px 0', fontWeight: 600 }}>Calls</th>
                <th style={{ padding: '4px 8px 6px 0', fontWeight: 600 }}>p50</th>
                <th style={{ padding: '4px 8px 6px 0', fontWeight: 600 }}>p95</th>
                <th style={{ padding: '4px 0 6px 0', fontWeight: 600 }}>Errors</th>
              </tr>
            </thead>
            <tbody>
              {stats.tools.map((t) => (
                <tr key={t.tool} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ padding: '6px 8px 6px 0', fontWeight: 600 }}>{t.tool}</td>
                  <td style={{ padding: '6px 8px 6px 0' }}>{t.count}</td>
                  <td style={{ padding: '6px 8px 6px 0' }}>{fmtMs(t.p50)}</td>
                  <td style={{ padding: '6px 8px 6px 0' }}>
                    {fmtMs(t.p95)}{' '}
                    {t.p95 != null && t.p95 > VOICE_TOOL_BUDGET_MS && <span className="cs-badge b-warn">over budget</span>}
                  </td>
                  <td style={{ padding: '6px 0' }}>
                    {t.errors > 0 ? <span className="cs-badge b-danger">{t.errors}</span> : '0'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// Break-glass log (orgs/{org}/supportAccess, written only by the operator's
// scripts/support-access.mjs BEFORE any out-of-band access). Staff have no
// in-app access to content, so an empty list is the customer-visible proof
// — say so rather than rendering a bare "nothing here".
const REASON_LABEL = {
  'support-request': 'Support request',
  'security-incident': 'Security incident',
  'legal-obligation': 'Legal obligation',
};

function OperatorAccess({ orgId }) {
  const { docs: rows } = useCollection(orgId ? `orgs/${orgId}/supportAccess` : null, { orderBy: 'startedAt', enabled: !!orgId });
  return (
    <section className="u-stack" style={{ '--gap': 'var(--sp-3)' }}>
      <div className="u-stack" style={{ '--gap': '2px' }}>
        <h2 style={{ margin: 0, fontSize: 'var(--fs-lg)' }}>Operator access</h2>
        <span className="u-xs u-muted">
          fast.legal staff have no in-app access to your content. Any access through infrastructure credentials — at your request, for a security incident, or under legal compulsion — is recorded here before it happens.
        </span>
      </div>
      {rows === null && <div className="cs-skeleton" style={{ height: 48 }} />}
      {rows !== null && !rows.length && (
        <div className="cs-empty">No one at fast.legal has accessed this organization's content.</div>
      )}
      {(rows || []).map((r) => (
        <div key={r.id} className="cs-card u-stack" style={{ padding: 'var(--sp-3) var(--sp-4)', '--gap': 'var(--sp-1)' }}>
          <div className="u-split">
            <span className="u-small" style={{ fontWeight: 700 }}>{REASON_LABEL[r.reason] || r.reason}</span>
            <span className={`cs-badge ${r.endedAt ? 'b-ok' : 'b-warn'}`}>{r.endedAt ? 'closed' : 'open'}</span>
          </div>
          <span className="u-small">{r.scope}</span>
          <span className="u-xs u-muted">
            {r.operator} · {fmtWhen(r.startedAt)}{r.endedAt ? ` → ${fmtWhen(r.endedAt)}` : ''}
            {r.ticket?.startsWith('grant:') ? ` · under a grant by ${r.requestedBy || 'your admin'}` : `${r.requestedBy ? ` · requested by ${r.requestedBy}` : ''}${r.ticket ? ` · ref ${r.ticket}` : ''}`}
          </span>
          {r.notes && <span className="u-xs u-muted">{r.notes}</span>}
        </div>
      ))}
    </section>
  );
}

const KIND_LABEL = {
  derive: 'AI refactor',
  ingest: 'Document ingest',
  graph: 'Knowledge graph build',
  index: 'Project brain indexing',
  'agent-edit': 'Agent edit',
};

// Brain-index tasks live on the deal (no docId) — link where it makes sense.
const workLink = (t) => (t.docId ? `/deals/${t.dealId}/docs/${t.docId}` : `/deals/${t.dealId}`);

// Backend task titles bake the kind in ("Knowledge graph — <doc>") but the
// row already labels the kind — repeating it just steals width from the part
// that actually distinguishes rows.
const displayTitle = (t) => String(t.title || t.kind).replace(/^(Knowledge graph|Project brain)\s+—\s+/, '');

function fmtDur(ms) {
  if (ms == null) return '—';
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return m < 60 ? `${m}m ${s % 60}s` : `${Math.floor(m / 60)}h ${m % 60}m`;
}

function fmtWhen(at) {
  if (!at) return '—';
  const d = new Date(at);
  const today = new Date().toDateString() === d.toDateString();
  return today ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function Activity() {
  const { orgId, role } = useOrg();
  const isAdmin = role === 'admin' || role === 'owner';
  const { docs: tasks } = useCollection(orgId ? `orgs/${orgId}/tasks` : null, { orderBy: 'startedAt', enabled: !!orgId && isAdmin });
  const { deals } = useVisibleDeals();
  const dealName = (id) => (deals || []).find((d) => d.id === id)?.name || id;

  if (!orgId) return <div className="cs-empty">Select an organization first.</div>;
  if (role && !['admin', 'owner'].includes(role)) {
    return <div className="cs-empty">Activity is visible to organization admins.</div>;
  }

  const running = (tasks || []).filter((t) => t.status === 'running');
  const recent = (tasks || []).filter((t) => t.status !== 'running').slice(0, 30);

  return (
    <div className="u-stack" style={{ '--gap': 'var(--sp-6)' }}>
      <div className="u-stack" style={{ '--gap': 'var(--sp-1)' }}>
        <h1 style={{ margin: 0 }}>Activity</h1>
        <span className="u-muted u-small">
          Every long-running backend job across the organization — live logs, step progress, ETA, and cost.
        </span>
      </div>

      <section className="u-stack" style={{ '--gap': 'var(--sp-3)' }}>
        <h2 style={{ margin: 0, fontSize: 'var(--fs-lg)' }}>
          Running {running.length > 0 && <span className="cs-badge b-info">{running.length}</span>}
        </h2>
        {tasks === null && <div className="cs-skeleton" style={{ height: 80 }} />}
        {tasks !== null && !running.length && <div className="cs-empty">Nothing running right now.</div>}
        {running.map((t) => (
          <div key={t.id} className="cs-card u-stack" style={{ '--gap': 'var(--sp-3)' }}>
            <div className="u-split">
              <div className="u-stack" style={{ '--gap': '2px', flex: 1, minWidth: 0 }}>
                <Link to={workLink(t)} style={{ textDecoration: 'none' }}>
                  <FitText style={{ fontWeight: 700 }} text={displayTitle(t)} />
                </Link>
                <span className="u-small u-muted">{KIND_LABEL[t.kind] || t.kind} · {dealName(t.dealId)}</span>
              </div>
              <span className="u-cluster" style={{ '--gap': 'var(--sp-2)', flexShrink: 0, alignItems: 'center' }}>
                <Link className="cs-btn cs-btn-outline cs-btn-sm" to={`/activity/${t.id}`}>Watch in cockpit</Link>
                <span className="cs-badge b-info">running</span>
              </span>
            </div>
            <TaskProgress task={t} progress={t} />
          </div>
        ))}
      </section>

      <section className="u-stack" style={{ '--gap': 'var(--sp-3)' }}>
        <h2 style={{ margin: 0, fontSize: 'var(--fs-lg)' }}>Recent</h2>
        {tasks !== null && !recent.length && <div className="cs-empty">No completed tasks yet.</div>}
        {recent.map((t) => (
          <details key={t.id} className="cs-card" style={{ padding: 'var(--sp-3) var(--sp-4)' }}>
            <summary className="u-split" style={{ cursor: 'pointer', listStyle: 'none' }}>
              <span className="u-stack" style={{ '--gap': '2px', flex: 1, minWidth: 0 }}>
                <FitText className="u-small" style={{ fontWeight: 700 }} text={displayTitle(t)} />
                <span className="u-xs u-muted">{KIND_LABEL[t.kind] || t.kind} · {dealName(t.dealId)} · {fmtWhen(t.finishedAt || t.updatedAt)}</span>
                {t.detail && <span className="u-xs u-muted u-clamp" style={{ '--lines': 2 }}>{t.detail}</span>}
              </span>
              <span className="u-cluster u-small u-tabular" style={{ '--gap': 'var(--sp-3)', flexShrink: 0, alignItems: 'baseline' }}>
                {t.costUsd > 0 && <span className="u-muted">${t.costUsd.toFixed(2)}</span>}
                <span className="u-muted">{fmtDur(t.runMs ?? (t.finishedAt ? t.finishedAt - t.startedAt : null))}</span>
                <span className={`cs-badge ${t.status === 'done' ? 'b-ok' : 'b-danger'}`}>{t.status}</span>
              </span>
            </summary>
            <div style={{ marginTop: 'var(--sp-3)' }}>
              <TaskProgress task={t} progress={t} />
              <div className="u-cluster" style={{ marginTop: 'var(--sp-2)', '--gap': 'var(--sp-2)' }}>
                <Link className="cs-btn cs-btn-outline cs-btn-sm" to={`/activity/${t.id}`}>Replay in cockpit</Link>
                {t.docId && <Link className="cs-btn cs-btn-outline cs-btn-sm" to={workLink(t)}>Open document</Link>}
              </div>
            </div>
          </details>
        ))}
      </section>

      <OperatorAccess orgId={orgId} />

      <VoiceHealth />
    </div>
  );
}
